import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { GeneralBackendService } from 'src/app/services/general-backend.service';
import { TravelBackendService } from 'src/app/services/travel-backend.service';
import { Approval } from 'src/shared/models/approval.model';
import { FinanceChargedDepartment } from 'src/shared/models/finance-charged-department.model';
import { TravelItinerary } from 'src/shared/models/travel-itinerary.model';
import { Appointment } from '../models/appointment.model';
import { Dependent } from '../models/dependent.model';
import { DropDownItem } from '../models/drop-down-item.model';
import { Link } from '../models/link.model';
import { NgxsForm } from '../models/ngxs-form.model';
import {
	TravelItinerarySearch,
	TravelItinerarySearchResponse,
} from '../models/travel-itinerary-search.model';
import { TravelItinerarySummary } from '../models/travel-itinerary-summary.model';
import { TravelPurpose } from '../models/travel-purpose.model';
import { TravelQueue } from '../models/travel-queue.model';
import { User } from '../models/user.model';
import { Logout } from './auth.action';
import { AuthState } from './auth.state';
import { StartLoading, StopLoading } from './main.action';
import {
	AddComment,
	ApproveRequest,
	CancelItinerary,
	ClearErrorMessage,
	ClearItinerary,
	ClearSearchResults,
	CloseAdvancedSearch,
	CreateItinerary,
	DeleteAttachment,
	DeleteItinerary,
	DenyRequest,
	GetAdministrativeQueue,
	GetAllTravelQueues,
	GetAppointments,
	GetApprovals,
	GetAttachmentMetadataTypes,
	GetCanEdit,
	GetCurrencies,
	GetDashboardTravelQueue,
	GetDependents,
	GetFinanceChargedDepartments,
	GetItinerary,
	GetManagerQueue,
	GetMyEmployees,
	GetMyTravel,
	GetOtherCostTypes,
	GetPerDiemLinks,
	GetRecentApprovals,
	GetTravelAdminQueue,
	GetTravelLegTypes,
	GetTravelPurposes,
	GetTravelers,
	OpenAdvancedSearch,
	ResetCreateItineraryForm,
	Search,
	SetAppointments,
	SetDependents,
	SetErrorMessage,
	SubmitItinerary,
	UpdateFinance,
	UpdateItinerary,
	UpdateLodgings,
	UpdateOtherCosts,
	UpdateTransportation,
	UpdateTravelDependents,
	UpdateTravelPartners,
	UploadAttachment,
} from './travel.action';
import { TravelStateModel } from './travel.model';

export const DEFAULT_STATE: TravelStateModel = {
	approvals: null,
	recentApprovals: [],
	financeChargedDepartments: [],
	dashboardTravelQueue: null,
	administrativeQueue: null,
	travelAdminQueue: null,
	managerQueue: null,
	myTravel: null,
	itinerary: null,
	createItineraryForm: {
		model: undefined,
		dirty: false,
		status: '',
		errors: {},
	},
	travelers: [],
	dependents: [],
	appointments: [],
	travelPurposes: [],
	currencies: [],
	travelLegTypes: [],
	otherCostTypes: [],
	attachmentMetadataTypes: [],
	canEditItinerary: false,
	hasPermission: false,
	canDelete: false,
	canApprove: false,
	errorMessage: null,
	searchOptions: null,
	searchResults: null,
	searchForm: {
		model: {},
		dirty: false,
		status: '',
		errors: {},
	},
	myEmployees: [],
	perDiemLinks: [],
	advancedSearch: false,
};

@State<TravelStateModel>({
	name: 'travel',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class TravelState {
	private travelBackend = inject(TravelBackendService);
	private generalBackend = inject(GeneralBackendService);
	private store = inject(Store);
	private router = inject(Router);
	private ngZone = inject(NgZone);

	@Selector()
	static travelState(state: TravelStateModel): TravelStateModel {
		return state;
	}

	@Selector()
	static approvals(state: TravelStateModel): Approval[] {
		return state.approvals;
	}

	@Selector()
	static hasApprovals(state: TravelStateModel): boolean {
		return state.approvals?.length > 0;
	}

	@Selector()
	static recentApprovals(state: TravelStateModel): Approval[] {
		return state.recentApprovals;
	}

	@Selector()
	static hasRecentApprovals(state: TravelStateModel): boolean {
		return state.recentApprovals?.length > 0;
	}

	@Selector()
	static financeChargedDepartments(
		state: TravelStateModel
	): FinanceChargedDepartment[] {
		return state.financeChargedDepartments;
	}

	@Selector()
	static administrativeQueue(state: TravelStateModel): TravelQueue {
		return state.administrativeQueue;
	}

	@Selector()
	static travelAdminQueue(state: TravelStateModel): TravelQueue {
		return state.travelAdminQueue;
	}

	@Selector()
	static dashboardTravelQueue(
		state: TravelStateModel
	): TravelItinerarySummary[] {
		return state.dashboardTravelQueue;
	}

	@Selector()
	static managerQueue(state: TravelStateModel): TravelQueue {
		return state.managerQueue;
	}

	@Selector()
	static myTravel(state: TravelStateModel): TravelQueue {
		return state.myTravel;
	}

	@Selector()
	static hasMyTravel(state: TravelStateModel): boolean {
		return state.myTravel?.queue?.length > 0;
	}

	@Selector()
	static itinerary(state: TravelStateModel): TravelItinerary {
		return state.itinerary;
	}

	@Selector()
	static itineraryTravelPurpose(state: TravelStateModel): TravelPurpose {
		if (state.itinerary.isTAR() && state.itinerary.travelPurpose) {
			const travelPurpose: TravelPurpose = state.travelPurposes.find(
				(t: TravelPurpose) =>
					t.purpose === state.itinerary.travelPurpose
			);
			return travelPurpose ? travelPurpose : null;
		}
		return null;
	}

	@Selector()
	static createItineraryForm(state: TravelStateModel): NgxsForm {
		return state.createItineraryForm;
	}

	@Selector()
	static travelers(state: TravelStateModel): User[] {
		return state.travelers;
	}

	@Selector()
	static dependents(state: TravelStateModel): Dependent[] {
		return state.dependents;
	}

	@Selector()
	static appointments(state: TravelStateModel): Appointment[] {
		return state.appointments;
	}

	@Selector()
	static travelPurposes(state: TravelStateModel): TravelPurpose[] {
		return state.travelPurposes;
	}

	@Selector()
	static currencies(state: TravelStateModel): DropDownItem[] {
		return state.currencies;
	}

	@Selector()
	static travelLegTypes(state: TravelStateModel): DropDownItem[] {
		return state.travelLegTypes;
	}

	@Selector()
	static otherCostTypes(state: TravelStateModel): DropDownItem[] {
		return state.otherCostTypes;
	}

	@Selector()
	static attachmentMetadataTypes(state: TravelStateModel): DropDownItem[] {
		return state.attachmentMetadataTypes;
	}

	@Selector()
	static canEditItinerary(state: TravelStateModel): boolean {
		return state.canEditItinerary;
	}

	@Selector()
	static hasPermission(state: TravelStateModel): boolean {
		return state.hasPermission;
	}

	@Selector()
	static canDelete(state: TravelStateModel): boolean {
		return state.canDelete;
	}

	@Selector()
	static canApprove(state: TravelStateModel): boolean {
		return state.canApprove;
	}

	@Selector()
	static canApproveOrDelete(state: TravelStateModel): boolean {
		return state.canApprove || state.canDelete;
	}

	@Selector()
	static notPersonalTravel(state: TravelStateModel): boolean {
		return state.itinerary && state.itinerary.notPersonalTravel();
	}

	@Selector()
	static errorMessage(state: TravelStateModel): string {
		return state.errorMessage;
	}

	@Selector()
	static searchOptions(state: TravelStateModel): TravelItinerarySearch {
		return state.searchOptions;
	}

	@Selector()
	static searchResults(state: TravelStateModel): TravelItinerarySummary[] {
		return state.searchResults;
	}

	@Selector()
	static myEmployees(state: TravelStateModel): User[] {
		return state.myEmployees;
	}

	@Selector()
	static searchForm(state: TravelStateModel): NgxsForm {
		return state.searchForm;
	}

	@Selector()
	static perDiemLinks(state: TravelStateModel): Link[] {
		return state.perDiemLinks;
	}

	@Selector()
	static advancedSearch(state: TravelStateModel): boolean {
		return state.advancedSearch;
	}

	/**
	 * GetApprovals: retrieves Approvals from backend
	 */
	@Action(GetApprovals)
	getApprovals(ctx: StateContext<TravelStateModel>, _action: GetApprovals) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.approvals().pipe(
			tap((approvals) => {
				ctx.patchState({ approvals: approvals });
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * GetRecentApprovals: retrieves Recent Approvals from backend
	 */
	@Action(GetRecentApprovals)
	getRecentApprovals(
		ctx: StateContext<TravelStateModel>,
		_action: GetRecentApprovals
	) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.recentApprovals().pipe(
			tap((recentApproval) => {
				ctx.patchState({ recentApprovals: recentApproval });
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * GetFinanceChargedDepartments: retrieves Finance Charged Departments from backend
	 */
	@Action(GetFinanceChargedDepartments)
	getFinanceChargedDepartments(
		ctx: StateContext<TravelStateModel>,
		_action: GetFinanceChargedDepartments
	) {
		if (!this.store.selectSnapshot(AuthState.isFinance)) {
			return;
		}
		return this.travelBackend
			.financeChargedDepartments()
			.pipe(
				tap((departments) =>
					ctx.patchState({ financeChargedDepartments: departments })
				)
			);
	}

	/**
	 * ApproveRequest: approves a given Approval
	 */
	@Action(ApproveRequest)
	approveRequest(
		ctx: StateContext<TravelStateModel>,
		action: ApproveRequest
	) {
		ctx.dispatch([new ClearErrorMessage(), new StartLoading()]);
		return this.travelBackend.approve(action.approval).pipe(
			tap(() => ctx.dispatch([new StopLoading(), new GetApprovals()])),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * DenyRequest: denies a given Approval
	 */
	@Action(DenyRequest)
	denyRequest(ctx: StateContext<TravelStateModel>, action: DenyRequest) {
		ctx.dispatch([new ClearErrorMessage(), new StartLoading()]);
		return this.travelBackend.deny(action.denial).pipe(
			tap(() => ctx.dispatch([new StopLoading(), new GetApprovals()])),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * GetDashboardTravelQueue: fetches the Dashboard's Travel Queue
	 */
	@Action(GetDashboardTravelQueue)
	getDashboardTravelQueue(
		ctx: StateContext<TravelStateModel>,
		_action: GetDashboardTravelQueue
	) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.dashboardTravelQueue().pipe(
			tap((summaries) => {
				ctx.patchState({ dashboardTravelQueue: summaries });
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * GetAllTravelQueues: fetches the initial Travel Queue pages
	 */
	@Action(GetAllTravelQueues)
	getAllTtravelQueues(
		ctx: StateContext<TravelStateModel>,
		_action: GetAllTravelQueues
	) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.allTtravelQueues().pipe(
			tap((response) => {
				ctx.patchState({
					administrativeQueue: TravelQueue.fromJSON(
						response.administrative
					),
					travelAdminQueue: TravelQueue.fromJSON(
						response.travelAdmin
					),
					managerQueue: TravelQueue.fromJSON(response.manager),
				});
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * GetAdministrativeQueue: fetches a particular page of the Administrative Queue
	 */
	@Action(GetAdministrativeQueue)
	getAdministrativeQueue(
		ctx: StateContext<TravelStateModel>,
		action: GetAdministrativeQueue
	) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.administrativeQueue(action.page).pipe(
			tap((queue) => {
				ctx.patchState({ administrativeQueue: queue });
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * GetTravelAdminQueue: fetches a particular page of the Travel Admin Queue
	 */
	@Action(GetTravelAdminQueue)
	getTravelAdminQueue(
		ctx: StateContext<TravelStateModel>,
		action: GetTravelAdminQueue
	) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.travelAdminQueue(action.page).pipe(
			tap((queue) => {
				ctx.patchState({ travelAdminQueue: queue });
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * GetManagerQueue: fetches a particular page of the Manager Queue
	 */
	@Action(GetManagerQueue)
	getManagerQueue(
		ctx: StateContext<TravelStateModel>,
		action: GetManagerQueue
	) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.managerQueue(action.page).pipe(
			tap((queue) => {
				ctx.patchState({ managerQueue: queue });
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * GetMyTravel: fetches a particular page of the travel queue for the current user
	 */
	@Action(GetMyTravel)
	getMyTravel(ctx: StateContext<TravelStateModel>, action: GetMyTravel) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.myTravel(action.page).pipe(
			tap((queue) => {
				ctx.patchState({ myTravel: queue });
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * GetItinerary: fetches selected itinerary from backend and updates state
	 */
	@Action(GetItinerary)
	getItinerary(ctx: StateContext<TravelStateModel>, action: GetItinerary) {
		ctx.dispatch([new StartLoading()]);
		ctx.patchState({
			itinerary: null,
			canEditItinerary: false,
			hasPermission: false,
			canDelete: false,
			canApprove: false,
		});
		return this.travelBackend.itinerary(action.request).pipe(
			tap((response) => {
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
					canEditItinerary: false,
					hasPermission: false,
					canDelete: false,
					canApprove: false,
				});
				ctx.dispatch([
					new StopLoading(),
					new GetCanEdit(action.request),
				]);
			})
		);
	}

	/**
	 * ClearItinerary: Sets `itinerary` to null
	 */
	@Action(ClearItinerary)
	clearItinerary(
		ctx: StateContext<TravelStateModel>,
		_action: ClearItinerary
	) {
		ctx.patchState({
			itinerary: null,
			canEditItinerary: false,
			hasPermission: false,
			canDelete: false,
			canApprove: false,
		});
	}

	/**
	 * GetCanEdit: Fetches & sets `canEditItinerary`
	 */
	@Action(GetCanEdit)
	getCanEdit(ctx: StateContext<TravelStateModel>, action: GetCanEdit) {
		return this.travelBackend.canEdit(action.request).pipe(
			tap((response) => {
				ctx.patchState({
					canEditItinerary: response.canEdit,
					hasPermission: response.hasPermission,
					canDelete: response.canDelete,
					canApprove: response.canApprove,
				});
			})
		);
	}

	/**
	 * SetErrorMessage: Updates `errorMessage` with a provided message.
	 */
	@Action(SetErrorMessage)
	setErrorMessage(
		ctx: StateContext<TravelStateModel>,
		action: SetErrorMessage
	) {
		ctx.patchState({
			errorMessage: action.errorMessage,
		});
	}

	/**
	 * ClearErrorMessage: Sets `errorMessage` to null
	 */
	@Action(ClearErrorMessage)
	clearErrorMessage(
		ctx: StateContext<TravelStateModel>,
		_action: ClearErrorMessage
	) {
		ctx.patchState({
			errorMessage: null,
		});
	}

	/**
	 * Search: Search for Travel Itineraries
	 */
	@Action(Search)
	search(ctx: StateContext<TravelStateModel>, action: Search) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		ctx.patchState({ searchOptions: action.options });
		return this.travelBackend.search(action.options).pipe(
			tap((response: TravelItinerarySearchResponse) => {
				ctx.patchState({
					searchResults: response.searchResult.map((result) =>
						TravelItinerarySummary.fromJSON(result)
					),
					searchOptions: TravelItinerarySearch.fromJSON(
						response.options
					),
				});
				ctx.dispatch(new StopLoading());
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	/**
	 * ClearSearchResults: Clears the travel search results
	 */
	@Action(ClearSearchResults)
	clearSearchResults(
		ctx: StateContext<TravelStateModel>,
		_action: ClearSearchResults
	) {
		ctx.patchState({ searchResults: null });
	}

	/**
	 * GetMyEmployees: Retrieves employees for the currently logged in manager
	 */
	@Action(GetMyEmployees)
	getMyEmployees(
		ctx: StateContext<TravelStateModel>,
		_action: GetMyEmployees
	) {
		if (!this.store.selectSnapshot(AuthState.isManager)) {
			return;
		}
		return this.travelBackend.myEmployees().pipe(
			tap((employees) =>
				ctx.patchState({
					myEmployees: employees,
				})
			)
		);
	}

	/**
	 * GetTravelers: Retrieves Users that can be used for creating a new itinerary
	 */
	@Action(GetTravelers)
	getTravelers(ctx: StateContext<TravelStateModel>, _action: GetTravelers) {
		return this.travelBackend.travelers().pipe(
			tap((users) =>
				ctx.patchState({
					travelers: users,
				})
			)
		);
	}

	/**
	 * SetDependents: Sets the `dependents` state property
	 */
	@Action(SetDependents)
	setDependents(ctx: StateContext<TravelStateModel>, action: SetDependents) {
		ctx.patchState({
			dependents: action.dependents,
		});
	}

	/**
	 * GetDependents: Retrieves Dependents for the provided `userId`
	 */
	@Action(GetDependents)
	getDependents(ctx: StateContext<TravelStateModel>, action: GetDependents) {
		ctx.patchState({
			dependents: [],
		});
		return this.travelBackend.getDependents(action.request).pipe(
			tap((dependents) => {
				ctx.patchState({
					dependents: dependents,
				});
			})
		);
	}

	/**
	 * SetAppointments: Sets the `appointments` state property
	 */
	@Action(SetAppointments)
	setAppointments(
		ctx: StateContext<TravelStateModel>,
		action: SetAppointments
	) {
		ctx.patchState({
			appointments: action.appointments,
		});
	}

	/**
	 * GetAppointments: Retrieves Dependents for the provided `userId`
	 */
	@Action(GetAppointments)
	getAppointments(
		ctx: StateContext<TravelStateModel>,
		action: GetAppointments
	) {
		ctx.patchState({
			appointments: [],
		});
		return this.travelBackend.getAppointments(action.request).pipe(
			tap((appointments) => {
				ctx.patchState({
					appointments: appointments,
				});
			})
		);
	}

	/**
	 * GetTravelPurposes: Retrieves TravelPurpose objects and sets them to travelPurposes state property
	 */
	@Action(GetTravelPurposes)
	getTravelPurposes(
		ctx: StateContext<TravelStateModel>,
		_action: GetTravelPurposes
	) {
		return this.travelBackend.getTravelPurposes().pipe(
			tap((travelPurposes) => {
				ctx.patchState({
					travelPurposes: travelPurposes,
				});
			})
		);
	}

	/**
	 * GetCurrencies: Retrieves currency DropDownItems and sets them to currencies state property
	 */
	@Action(GetCurrencies)
	getCurrencies(ctx: StateContext<TravelStateModel>, _action: GetCurrencies) {
		return this.generalBackend.getCurrencies().pipe(
			tap((items) => {
				ctx.patchState({
					currencies: items,
				});
			})
		);
	}

	/**
	 * AddComment: Adds a Comment, and updates comments of the current Itinerary
	 */
	@Action(AddComment)
	addComment(ctx: StateContext<TravelStateModel>, action: AddComment) {
		ctx.dispatch(new StartLoading());
		return this.travelBackend.addComment(action.comment).pipe(
			tap((response) => {
				ctx.patchState({
					itinerary: TravelItinerary.patch(ctx.getState().itinerary, {
						comments: response,
					}),
				});
				ctx.dispatch(new StopLoading());
			})
		);
	}

	/**
	 * ResetCreateItineraryForm: Resets the Create Itinerary Form's State
	 */
	@Action(ResetCreateItineraryForm)
	resetCreateItineraryForm(
		ctx: StateContext<TravelStateModel>,
		_action: ResetCreateItineraryForm
	) {
		ctx.patchState({
			createItineraryForm: {
				model: {},
				dirty: false,
				status: '',
				errors: {},
			},
		});
	}

	/**
	 * CreateItinerary: Creates a new Travel Itinerary
	 */
	@Action(CreateItinerary)
	createItinerary(
		ctx: StateContext<TravelStateModel>,
		action: CreateItinerary
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.createItinerary(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch([
					new StopLoading(),
					new ResetCreateItineraryForm(),
				]);
				this.ngZone.run(() => {
					this.router.navigate([
						'tacit',
						'travel',
						'itinerary',
						response.id,
					]);
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * UpdateItinerary: Submits the updated general information to the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(UpdateItinerary)
	updateItinerary(
		ctx: StateContext<TravelStateModel>,
		action: UpdateItinerary
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.updateItinerary(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * UpdateTravelDependents: Submits the updated travel dependents to the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(UpdateTravelDependents)
	updateTravelDependents(
		ctx: StateContext<TravelStateModel>,
		action: UpdateTravelDependents
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.updateTravelDependents(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * UpdateTravelPartners: Submits the updated travel partners to the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(UpdateTravelPartners)
	updateTravelPartners(
		ctx: StateContext<TravelStateModel>,
		action: UpdateTravelPartners
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.updateTravelPartners(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * UpdateTransportation: Submits the updated Transportation Groups / Legs to the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(UpdateTransportation)
	updateTransportation(
		ctx: StateContext<TravelStateModel>,
		action: UpdateTransportation
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.updateTransportation(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * UpdateLodgings: Submits the updated Lodgings to the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(UpdateLodgings)
	updateLodgings(
		ctx: StateContext<TravelStateModel>,
		action: UpdateLodgings
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.updateLodgings(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * UpdateOtherCosts: Submits the updated Other Costs to the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(UpdateOtherCosts)
	updateOtherCosts(
		ctx: StateContext<TravelStateModel>,
		action: UpdateOtherCosts
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.updateOtherCosts(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * UpdateFinance: Submits the updated Finance fields to the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(UpdateFinance)
	updateFinance(ctx: StateContext<TravelStateModel>, action: UpdateFinance) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.updateFinance(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * UploadAttachment: Uploads an attachment to the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(UploadAttachment)
	uploadAttachment(
		ctx: StateContext<TravelStateModel>,
		action: UploadAttachment
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.uploadAttachment(action.request).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * DeleteAttachment: Deletes an attachment from the backend
	 * and then patches the `itinerary` state property with the response.
	 */
	@Action(DeleteAttachment)
	deleteAttachment(
		ctx: StateContext<TravelStateModel>,
		action: DeleteAttachment
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.deleteAttachment(action.request).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	@Action(GetTravelLegTypes)
	getTravelLegTypes(
		ctx: StateContext<TravelStateModel>,
		_action: GetTravelLegTypes
	) {
		return this.generalBackend.getTravelLegTypes().pipe(
			tap((items) => {
				ctx.patchState({
					travelLegTypes: items,
				});
			})
		);
	}

	@Action(GetOtherCostTypes)
	getOtherCostTypes(
		ctx: StateContext<TravelStateModel>,
		_action: GetOtherCostTypes
	) {
		return this.generalBackend.getOtherCostTypes().pipe(
			tap((items) => {
				ctx.patchState({
					otherCostTypes: items,
				});
			})
		);
	}

	@Action(GetAttachmentMetadataTypes)
	getAttachmentMetadataTypes(
		ctx: StateContext<TravelStateModel>,
		_action: GetAttachmentMetadataTypes
	) {
		return this.generalBackend.getAttachmentMetadataTypes().pipe(
			tap((items) => {
				ctx.patchState({
					attachmentMetadataTypes: items,
				});
			})
		);
	}

	/**
	 * SubmitItinerary: Submits the Travel Itinerary and then patches the `itinerary` state property.
	 */
	@Action(SubmitItinerary)
	submitItinerary(
		ctx: StateContext<TravelStateModel>,
		action: SubmitItinerary
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.submit(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch([
					new StopLoading(),
					new GetCanEdit(action.itinerary),
				]);
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * CancelItinerary: Cancels/Unsubmits the Travel Itinerary and then patches the `itinerary` state property.
	 */
	@Action(CancelItinerary)
	cancelItinerary(
		ctx: StateContext<TravelStateModel>,
		action: CancelItinerary
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.cancel(action.itinerary).pipe(
			tap((response) => {
				ctx.dispatch([
					new StopLoading(),
					new GetCanEdit(action.itinerary),
				]);
				ctx.patchState({
					itinerary:
						TravelItinerary.fromTravelItineraryResponse(response),
				});
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * DeleteItinerary: Deletes the Travel Itinerary.
	 */
	@Action(DeleteItinerary)
	deleteItinerary(
		ctx: StateContext<TravelStateModel>,
		action: DeleteItinerary
	) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.travelBackend.delete(action.itinerary).pipe(
			tap((_response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					itinerary: null,
				});
				this.ngZone.run(() =>
					this.router.navigate(['tacit', 'dashboard'])
				);
			}),
			catchError((error) => {
				ctx.dispatch([new SetErrorMessage(error), new StopLoading()]);
				return of({});
			})
		);
	}

	/**
	 * GetPerDiemLinks: Fetches links from backup and updates the state
	 */
	@Action(GetPerDiemLinks)
	getPerDiemLinks(
		ctx: StateContext<TravelStateModel>,
		_action: GetPerDiemLinks
	) {
		return this.generalBackend.getPerDiemLinks().pipe(
			tap((links) => {
				ctx.patchState({ perDiemLinks: links });
			})
		);
	}

	@Action(OpenAdvancedSearch)
	openAdvancedSearch(
		ctx: StateContext<TravelStateModel>,
		_action: OpenAdvancedSearch
	) {
		if (ctx.getState().advancedSearch === false) {
			ctx.patchState({
				advancedSearch: true,
			});
		}
	}

	@Action(CloseAdvancedSearch)
	closeAdvancedSearch(
		ctx: StateContext<TravelStateModel>,
		_action: CloseAdvancedSearch
	) {
		if (ctx.getState().advancedSearch === true) {
			ctx.patchState({
				advancedSearch: false,
			});
		}
	}

	@Action(Logout)
	logout(ctx: StateContext<TravelStateModel>, _action: Logout) {
		ctx.patchState(DEFAULT_STATE);
	}
}
